import { ChannelListService } from './../../core/service/grid-data/grid-data-channel.service';
import { Component, Input, Output, EventEmitter, ViewChild, OnInit, ElementRef } from '@angular/core';
import { AssessmentEdit } from '../model/assessment.edit';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { merge, cloneDeep, sortBy } from 'lodash';
import { AssessmentService } from 'src/app/core/service/assessment.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { FileRestrictions, UploadEvent } from '@progress/kendo-angular-upload';
import { TranslateService } from '@ngx-translate/core';
import { SelectEvent, TabStripComponent } from '@progress/kendo-angular-layout';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { MessengerService, MessageSubject } from 'src/app/core/service/messenger.service';
import { TaskService } from 'src/app/core/service/task.service';
import { Code, Dictionary } from 'src/app/core/model/dictionary';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { CaseUtility } from 'src/app/case/case.utility';
import { SettingService } from 'src/app/core/service/setting.service';
import { CustomFile } from 'src/app/message/model/custom-file';
import { CommunityServiceCategoryService } from 'src/app/core/service/community-service-category.service';
import { Guid } from 'guid-typescript';
import { forkJoin } from 'rxjs';
import { AssessmentListService } from 'src/app/core/service/grid-data/grid-data-assessment.service';
import { UserService } from 'src/app/core/service/user.service';
import { User } from 'src/app/user/model/user';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { GridEventHandler } from 'src/app/core/model/grid-settings';
import { RuleService } from 'src/app/core/service/rule.service';
import { Base64Util } from 'src/app/core/utility/base64-util';
import { Base64 } from 'js-base64';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { Organization } from 'src/app/core/model/organization';
import { OrganizationService } from 'src/app/core/service/organization.service';

@Component({
  standalone: false,
  selector: 'app-assessment-edit',
  templateUrl: './assessment-edit.component.html',
  styleUrls: ['./assessment-edit.component.scss'],
  providers: [AssessmentListService]    // Create another instance of the list to not interfer with the admin list
})
export class AssessmentEditComponent implements OnInit
{
  @ViewChild('tabstrip') public tabstrip: TabStripComponent;
  @ViewChild(TooltipDirective) public tooltipDir: TooltipDirective;

  @Input() public isNew = false;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public save: EventEmitter<AssessmentEdit> = new EventEmitter();

  public editForm: any;
  public editFormActive = false;
  public assessmentTypes = [];
  public allAssessmentTypes = [];
  public selectedFile: any = null;
  public invalidFileExtensionText: string;
  public isDeleted = false;
  public assessmentId: number;
  public communityEnabled = false;
  public patientLabel = {};

  // Formly model and fields
  public model = {};
  public fields: FormlyFieldConfig[] = [];
  public flags = [];
  public flagsFormValid: boolean;
  public flagsLookup: Code[];
  public flagCategoriesLookup: Code[];
  public flagPrioritiesLookup: Code[];

  public tasks = [];
  public tasksFormValid: boolean;
  public serviceTypes = [];
  public serviceTypesFormValid: boolean;
  public serviceTypesLookup: Code[];
  public channels = [];
  public channelsFormValid: boolean;
  public needs = [];
  public needsFormValid: boolean;
  public serviceCategoriesLookup: any = [];
  public assessmentsLookup: any = [];
  public channelsLookup: any = [];
  public staffList = [];
  public view: GridViewManager;
  public count = 0;
  public recordDisabledText = '';
  public recordDeletedText = '';
  public dialogVisible: boolean = false;

  public allOrganizations = [];
  public cachedOrganizations = [];  
  public organizationsFiltered: Organization[] = [];
  public virtualOrganization: any = { itemHeight: 30 };

  /**
   * The newly added staff member.
   */
  public newStaffMember = null;

  /**
   * Filtered list of staff candidates for drop-down.
   */
  public staffCandidatesFiltered: User[] = [];

  /**
   * Assessment staff.
   */
  public staff = [];

  /**
   * All sensitivities codes
   */
  public allSensitivities = [];

  /**
   * Filtered list of sensitivities for drop-down.
   */
  public sensitivitiesFiltered: Dictionary[] = [];

  /**
   * Current search term entered from a comboxbox
   */
  public searchTerm: string;

  /**
   * Maximum number of items for sensitivity virtualization. This is to accelerate the DOM performance.
   */
  public virtualSensitivity: any = {
    itemHeight: 30
  };

  // Image restrictions
  public fileRestrictions: FileRestrictions =
    {
      allowedExtensions: ['.pdf', '.doc', '.docx'],
      maxFileSize: 2000
    };

  public fileImportRestrictions: FileRestrictions =
    {
      allowedExtensions: ['.json']
    };

  public windowState = 'default';
  public selectedTab = 0;

  private assessmentEdit: AssessmentEdit;

  /**
   * Candidate users for assessment staff drop-down.
   */
  private staffCandidates = [];

  constructor(
    private fb: UntypedFormBuilder,
    private assessmentService: AssessmentService,
    private translate: TranslateService,
    private messengerService: MessengerService,
    private messageHandlerService: MessageHandlerService,
    private taskService: TaskService,
    private dictionaryService: DictionaryService,
    private caseUtilityService: CaseUtility,
    private settingService: SettingService,
    private ruleService: RuleService,
    private assessmentListService: AssessmentListService,
    private channelListService: ChannelListService,
    private communityServiceCategoryService: CommunityServiceCategoryService,
    private userService: UserService,
    private elem: ElementRef,
    private organizationService: OrganizationService,)
  {
    // Get sensitivities
    this.dictionaryService.getCachedSensitivities().subscribe({
      next: (data) =>
      {
        this.allSensitivities = data;
        this.filterSensitivities('');
      }
    });

    this.dictionaryService.getCachedFlags().subscribe({
      next: (data) =>
      {
        data.forEach(d =>
        {
          if (d.displayName.includes('(') && d.displayName.includes(')'))
          {
            return;
          }
          d.displayName = `${d.displayName} (${d.code})`;
        });
        this.flagsLookup = data;
      }
    });

    this.dictionaryService.getCachedCommunityServiceTypes().subscribe({
      next: (data) => { this.serviceTypesLookup = sortBy(data, ['displayName']); }
    });

    this.dictionaryService.getCachedFlagPriorities().subscribe({
      next: (data) => { this.flagPrioritiesLookup = data; }
    });

    this.dictionaryService.getCachedFlagCategories().subscribe({
      next: (data) => { this.flagCategoriesLookup = data; }
    });
  }

  public get active(): UntypedFormControl
  {
    return this.editForm.get('active');
  }

  public get name(): UntypedFormControl
  {
    return this.editForm.get('name');
  }

  public get description(): UntypedFormControl
  {
    return this.editForm.get('description');
  }

  public get type(): UntypedFormControl
  {
    return this.editForm.get('type');
  }

  public get source(): UntypedFormControl
  {
    return this.editForm.get('source');
  }

  public get documentName(): UntypedFormControl
  {
    return this.editForm.get('documentName');
  }

  public get assessmentVersion(): UntypedFormControl
  {
    return this.editForm.get('assessmentVersion');
  }

  public get sensitivities(): UntypedFormControl
  {
    return this.editForm.get('sensitivities');
  }

  public get privateAssessment(): UntypedFormControl
  {
    return this.editForm.get('privateAssessment');
  }

  public get organizationalSpecific(): UntypedFormControl
  {
    return this.editForm.get('organizationalSpecific');
  }

  public get showMemberExtension(): UntypedFormControl
  {
    return this.editForm.get('showMemberExtension');
  }

  public get workflowOnly(): UntypedFormControl
  {
    return this.editForm.get('workflowOnly');
  }

  public get organizations(): UntypedFormControl
  {
    return this.editForm.get('organizations');
  }  

  @Input() public set formModel(assessmentEdit: AssessmentEdit)
  {
    this.initModel(assessmentEdit);
  }

  public ngOnInit(): void
  {
    this.view = new GridViewManager(99999);
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.communityEnabled = sessionSetting.communityEnabled;
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });

    this.userService.getCachedUsers().subscribe({
      next: (data) =>
      {
        this.staffList = data;
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });

    this.organizationService.getCachedOrganizations().subscribe({
      next: (data) =>
      {
        this.cachedOrganizations = data;
        this.allOrganizations = data;
        this.filterOrganizations('');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });       
  }

  /**
   * Filters the organizations based on user input.
   */
  public filterOrganizations(filter: string): void
  {
    this.organizationsFiltered = this.allOrganizations
      .filter(fa => fa.name.toLowerCase().includes(filter.toLowerCase()));
  }  

  public onTabSelect(e: SelectEvent): void
  {
    this.selectedTab = e.index;
  }

  /**
   * Save handler
   *
   * @param e Event
   */
  public onSave(e: Event): void
  {
    e.preventDefault();
    const objectToPersist = merge(this.assessmentEdit, this.editForm.value);

    if (this.source.value === 'STATIC')
    {
      if (this.selectedFile !== null)
      {
        const base64 = this.selectedFile.split('base64,')[1];
        objectToPersist.file = base64;
      }
      delete objectToPersist.flags;
      delete objectToPersist.tasks;
      delete objectToPersist.serviceTypes;
      delete objectToPersist.channels;
      delete objectToPersist.needs;
    }
    else
    {
      if (this.fields.length > 0)
      {
        objectToPersist.file = Base64Util.encodeUnicode(JSON.stringify(this.fields));
        objectToPersist.documentMimeType = 'application/json';
        objectToPersist.documentName = this.name.value.replace(/-/g, '_').replace(/ /g, '_') + '.json';
      }
      else
      {
        this.messageHandlerService.displayError('NO-FORM-DATA-SET');
        return;
      }

      // Set flags object to persist
      let flagsToPersist = cloneDeep(this.flags);
      if (flagsToPersist !== undefined)
      {
        // Validate flags query rules
        const flagRulesValid = this.isValidRules(flagsToPersist);
        if (!flagRulesValid)
        {
          this.raiseInvalidRuleError('ASSESSMENT-NO-FLAG-RULES-SELECTED', 'selectFormlyDesignerTabFlag');
          return;
        }

        flagsToPersist.forEach(f =>
        {
          const query = f.query;
          this.ruleService.convertRulesValueArrayToString(query);
          this.ruleService.convertRulesValueDateTimeToUTC(query);
          f.query = Base64Util.encodeUnicode(JSON.stringify(query));
          f.privacy = f.privacy ? 'privated' : 'shared';

          // Set Flag Type reference if selected from list
          const flagType = this.flagsLookup.find(x => x.displayName === f.description);
          f.flagType = (flagType !== undefined) ? flagType : null;

          // Remove temporary variables
          delete f.showRules;
          delete f.guid;
          delete f.importError;
          delete f.allFlags;
          delete f.allFlagsFiltered;
          delete f.currentDictionaryUsed;
          delete f.previousDictionaryUsed;
        });
      }
      else
      {
        flagsToPersist = [];
      }
      objectToPersist.flags = flagsToPersist;

      // Set tasks object to persist
      let tasksToPersist = cloneDeep(this.tasks);
      if (tasksToPersist !== undefined)
      {
        // Validate tasks query rules
        const taskRulesValid = this.isValidRules(tasksToPersist);
        if (!taskRulesValid)
        {
          this.raiseInvalidRuleError('ASSESSMENT-NO-TASK-RULES-SELECTED', 'selectFormlyDesignerTabTask');
          return;
        }

        tasksToPersist.forEach(f =>
        {
          const query = f.query;
          this.ruleService.convertRulesValueArrayToString(query);
          this.ruleService.convertRulesValueDateTimeToUTC(query);
          f.query = Base64Util.encodeUnicode(JSON.stringify(query));
          f.priority = f.priority.value;

          // Remove temporary variables
          delete f.showRules;
          delete f.guid;
          delete f.importError;
        });
      }
      else
      {
        tasksToPersist = [];
      }
      objectToPersist.tasks = tasksToPersist;

      // Set service types object to persist
      let serviceTypesToPersist = cloneDeep(this.serviceTypes);
      if (serviceTypesToPersist !== undefined)
      {
        // Validate service types query rules
        const serviceTypesRulesValid = this.isValidRules(serviceTypesToPersist);
        if (!serviceTypesRulesValid)
        {
          this.raiseInvalidRuleError('ASSESSMENT-NO-SERVICE-TYPES-RULES-SELECTED', 'selectFormlyDesignerTabServiceType');
          return;
        }

        serviceTypesToPersist.forEach(f =>
        {
          const query = f.query;
          this.ruleService.convertRulesValueArrayToString(query);
          this.ruleService.convertRulesValueDateTimeToUTC(query);
          f.query = Base64Util.encodeUnicode(JSON.stringify(query));

          // Remove temporary variables
          delete f.showRules;
          delete f.guid;
          delete f.importError;
        });
      }
      else
      {
        serviceTypesToPersist = [];
      }
      objectToPersist.serviceTypes = serviceTypesToPersist;

      // Set channels object to persist
      let channelsToPersist = cloneDeep(this.channels);
      if (channelsToPersist !== undefined)
      {
        // Validate channels query rules
        const channelsRulesValid = this.isValidRules(channelsToPersist);
        if (!channelsRulesValid)
        {
          this.raiseInvalidRuleError('ASSESSMENT-NO-CHANNELS-RULES-SELECTED', 'selectFormlyDesignerTabChannel');
          return;
        }

        channelsToPersist.forEach(f =>
        {
          const query = f.query;
          this.ruleService.convertRulesValueArrayToString(query);
          this.ruleService.convertRulesValueDateTimeToUTC(query);
          f.query = Base64Util.encodeUnicode(JSON.stringify(query));
          f.priority = parseInt(f.priority.priority, 10);

          // Remove temporary variables
          delete f.showRules;
          delete f.guid;
          delete f.importError;
        });
      }
      else
      {
        channelsToPersist = [];
      }
      objectToPersist.channels = channelsToPersist;

      // Set needs object to persist
      let needsToPersist = cloneDeep(this.needs);
      if (needsToPersist !== undefined)
      {
        // Validate needs query rules
        const needsRulesValid = this.isValidRules(needsToPersist);
        if (!needsRulesValid)
        {
          this.raiseInvalidRuleError('ASSESSMENT-NO-NEEDS-RULES-SELECTED', 'selectFormlyDesignerTabNeed');
          return;
        }

        needsToPersist.forEach(f =>
        {
          const query = f.query;
          this.ruleService.convertRulesValueArrayToString(query);
          this.ruleService.convertRulesValueDateTimeToUTC(query);
          f.query = Base64Util.encodeUnicode(JSON.stringify(query));

          // Remove temporary variables
          delete f.showRules;
          delete f.guid;
          delete f.importError;
        });
      }
      else
      {
        needsToPersist = [];
      }
      objectToPersist.needs = needsToPersist;
    }

    // Sensitivities
    objectToPersist.sensitivities = this.sensitivities.value;

    // Private
    objectToPersist.privateAssessment = this.privateAssessment.value;
    if (this.privateAssessment.value)
    {
      objectToPersist.users = this.staff;
    }
    else
    {
      objectToPersist.users = [];
    }

    // Organizational Specific
    objectToPersist.organizationalSpecific = this.organizationalSpecific.value;
    if (this.organizationalSpecific.value)
    {
      objectToPersist.organizations = this.organizations.value;
    }
    else
    {
      objectToPersist.organizations = [];
    }    

    objectToPersist.workflowOnly = this.workflowOnly.value;
    objectToPersist.showMemberExtension = this.showMemberExtension.value;

    this.save.emit(objectToPersist);
    this.editFormActive = false;
  }

  public cancelSave(confirm: boolean): void
  {
    if (confirm)
    {
      this.selectedFile = null;
      this.editFormActive = false;
      this.cancelling.emit();
    }
    this.dialogVisible = false;
  }

  public openDialog(): void
  {
    if (this.editForm.dirty)
    {
      this.dialogVisible = true;
    }
    else
    {
      this.cancelSave(true);
    }
  }

  /**
   * Check if user from dropdown is disabled or enabled.
   *
   * @returns boolean
   */
  public itemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.active;
  }

  public assessmentItemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.active;
  }
  /**
   * File has been selected.
   *
   * @param event Event
   */
  public onFileChanged(event: Event): void
  {
    const input = event.target as HTMLInputElement;
    const fileList: FileList = input.files;
    if (fileList.length > 0)
    {
      const file: File = fileList[0];

      // Validate file size
      if ((file.size / 1024) > this.fileRestrictions.maxFileSize)
      {
        this.messageHandlerService.displayError('FILE-SIZE-EXCEED-LIMIT');
        return;
      }

      // Validate file extension
      let validExtension = false;
      this.fileRestrictions.allowedExtensions.forEach((ext: string) =>
      {
        if (file.name.toLowerCase().indexOf(ext) !== -1)
        {
          validExtension = true;
        }
      });
      if (!validExtension)
      {
        this.messageHandlerService.displayDirectMessage('error',
          this.invalidFileExtensionText.replace('{{value}}', this.fileRestrictions.allowedExtensions.join()));
        return;
      }
      this.assessmentEdit.documentName = file.name;
      this.assessmentEdit.documentMimeType = file.type;
      this.documentName.setValue(file.name);
      this.editForm.markAsDirty();
      this.setFileFromBlob(file);
    }
  }

  /**
   * Return the tab title key to use.
   */
  public getTabTitle(): string
  {
    return `ASSESSMENT-SOURCE-${this.source.value}`;
  }

  /**
   * Fields has been changed.
   *
   * @param e Event
   */
  public fieldsChanged(updatedFields: FormlyFieldConfig[]): void
  {
    this.editForm.markAsDirty();
    this.fields = updatedFields;
  }

  /**
   * Flags has been changed.
   *
   * @param e Event
   */
  public flagsChanged(updatedFlags: any): void
  {
    this.editForm.markAsDirty();
    this.flagsFormValid = updatedFlags.valid;
    this.flags = updatedFlags.value.flags;
  }

  /**
   * Tasks has been changed.
   *
   * @param e Event
   */
  public tasksChanged(updatedTasks: any): void
  {
    this.editForm.markAsDirty();
    this.tasksFormValid = updatedTasks.valid;
    this.tasks = updatedTasks.value.tasks;
  }

  /**
   * Service Types has been changed.
   *
   * @param e Event
   */
  public serviceTypesChanged(updatedServiceTypes: any): void
  {
    this.editForm.markAsDirty();
    this.serviceTypesFormValid = updatedServiceTypes.valid;
    this.serviceTypes = updatedServiceTypes.value.serviceTypes;
  }

  /**
   * Channels has been changed.
   *
   * @param e Event
   */
  public channelsChanged(updatedChannels: any): void
  {
    this.editForm.markAsDirty();
    this.channelsFormValid = updatedChannels.valid;
    this.channels = updatedChannels.value.channels;
  }

  /**
   * Needs has been changed.
   *
   * @param e Event
   */
  public needsChanged(updatedNeeds: any): void
  {
    this.editForm.markAsDirty();
    this.needsFormValid = updatedNeeds.valid;
    this.needs = updatedNeeds.value.needs;
  }

  public onImportSetup(): void
  {
    const uploadButton = this.elem.nativeElement.querySelectorAll('input[name="files"]');
    uploadButton[0].click();
  }

  /**
   * File has been selected.
   *
   * @param event Event
   */
  public onFileImportChanged(event: UploadEvent): void
  {
    event.files.forEach((file) =>
    {
      // Validate file extension
      let validExtension = false;
      this.fileImportRestrictions.allowedExtensions.forEach((ext: string) =>
      {
        if (file.name.toLowerCase().indexOf(ext) !== -1)
        {
          validExtension = true;
        }
      });
      if (!validExtension)
      {
        this.messageHandlerService.displayDirectMessage('error',
          this.invalidFileExtensionText.replace('{{value}}', this.fileImportRestrictions.allowedExtensions.join()));
        return;
      }

      const customFile = new CustomFile();
      customFile.name = file.name;
      customFile.type = file.rawFile.type;
      this.setImportFileFromBlob(customFile, file.rawFile);
    });
  }

  /**
   * Filters the sensitivities based on user input.
   */
  public filterSensitivities(filter: string): void
  {
    this.searchTerm = filter;
    this.sensitivitiesFiltered = this.allSensitivities
      .filter(user => user.displayName.toLowerCase().includes(filter.toLowerCase()));
  }

  /**
   * Filters the assessment staff based on user input.
   */
  public filterStaffCandidates(filterValue: string): void
  {
    this.searchTerm = filterValue;
    this.staffCandidatesFiltered = this.staffCandidates
      .filter(user => user.name.toLowerCase().includes(filterValue.toLowerCase()));
  }

  /**
   * Delete user from list.
   *
   *@param user User to delete.
   */
  public deleteStaff(user: User): void
  {
    this.staff = this.staff.filter(u => u.id !== user.id);
    this.view.state.skip = 0;
    this.view.setData(this.staff);
    this.editForm.markAsDirty();
    this.count = this.staff.length;
  }

  /**
   * Staff cancel handler
   */
  public staffCancelHandler(event: GridEventHandler): void
  {
    event.sender.closeRow(event.rowIndex);
  }

  /**
   * Staff add handler
   */
  public staffAddHandler(event: GridEventHandler): void
  {
    this.newStaffMember = null;
    this.staffCandidates = sortBy(this.staffList.filter(u => !this.staff.map(m => m.id).includes(u.id)), ['name']);
    this.filterStaffCandidates('');
    event.sender.addRow(new UntypedFormGroup({ editing: new UntypedFormControl(true) }));
  }

  /**
   * Add staff to grid in preparation for save.
   */
  public staffSaveHandler(event: GridEventHandler): void
  {
    event.sender.closeRow(event.rowIndex);
    this.staff.unshift(this.newStaffMember);
    this.staff = sortBy(this.staff, ['name']);
    this.view.state.skip = 0;
    this.view.setData(this.staff);
    this.count = this.staff.length;
    this.editForm.markAsDirty();
  }

  /**
   * Check if user from dropdown is disabled or enabled.
   *
   * @returns boolean
   */
  public userItemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.enabled && !itemArgs.dataItem.inRegistration;
  }

  public setRecordStateText(dataItem: any): string
  {
    if (dataItem && dataItem.deleted)
    {
      return this.recordDeletedText;
    }
    else if (dataItem && !dataItem.enabled)
    {
      return this.recordDisabledText;
    }
    else
    {
      return '';
    }
  }

  public showTooltip(e: ElementRef): void
  {
    this.tooltipDir.toggle(e, true);
  }

  public hideTooltip(): void
  {
    this.tooltipDir.hide();
  }

  /**
   * Check if query object has rules.
   *
   * @param objectToPersist Object to validate
   * @returns boolean True if the query object contains at least one rule.
   */
  private isValidRules(objectToPersist: any): boolean
  {
    let validRules = true;
    objectToPersist.forEach(x =>
    {
      if (validRules)
      {
        // Validate that each entry contains at least one query rules.
        if (x.query.rules.length === 0)
        {
          validRules = false;
        }
        else
        {
          validRules = this.isValidRulesConditionRecursive(x.query.rules, validRules);
        }
      }
    });
    return validRules;
  }

  /**
   * This method is validating recursively to make sure all rules have a value selected.
   *
   * @param rules Query rules arrayr
   * @param validRules Indicate if the rule is valid or not
   * @returns True if rule is valid and false is rule is not valid.
   */
  private isValidRulesConditionRecursive(rules: any, validRules: boolean): boolean
  {
    let valid = validRules;
    if (valid)
    {
      rules.forEach(r =>
      {
        if (r.rules)
        {
          valid = this.isValidRulesConditionRecursive(r.rules, valid);
        }
        else
        {
          // If no value selected or value is blank, raise a validation error.
          if (!r.value || r.value === '')
          {
            valid = false;
          }
        }
      });
    }
    return valid;
  }

  // Set Organizations validator based on organizationalSpecific value (if organizationalSpecific is YES, require at least one organization to be selected)
  private updateOrganizationsValidator(): void
  {
    const organizationalSpecificValue = this.organizationalSpecific?.value;
    const organizationsControl = this.editForm.get('organizations');
    
    if (organizationsControl)
    {
      if (organizationalSpecificValue)
      {
        const organizations = organizationsControl.value;
        if (!organizations || !Array.isArray(organizations) || organizations.length === 0)
        {
          organizationsControl.setValidators([Validators.required]);
          organizationsControl.markAsTouched();
        }
        else
        {
          organizationsControl.setValidators([]);
        }
      }
      else
      {
        organizationsControl.setValidators([]);
      }
      organizationsControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  /**
   * Raise Invalid Rule Error
   *
   * @param errorKey Error key to show.
   * @param tabEvent Tab event to trigger.
   */
  private raiseInvalidRuleError(errorKey: string, tabEvent: string): void
  {
    this.messageHandlerService.displayError(errorKey);
    setTimeout(() =>
    {
      this.tabstrip.selectTab(1);
      setTimeout(() =>
      {
        this.messengerService.sendMessage(new MessageSubject(tabEvent));
      });
    });
  }

  /**
   * Model initilization
   *
   * @param assessment Assessment edit object.
   */
  private initModel(assessment: AssessmentEdit): void
  {
    if (assessment === undefined)
    {
      return;
    }

    // Get the translation used when file types is invalid
    this.translate.get(['INVALID-FILE-TYPE', 'RECORD-DISABLED', 'RECORD-DELETED']).subscribe({
      next: (res) =>
      {
        this.invalidFileExtensionText = res['INVALID-FILE-TYPE'];
        this.recordDisabledText = res['RECORD-DISABLED'];
        this.recordDeletedText = res['RECORD-DELETED'];
      }
    });

    this.selectedTab = 0;
    this.isDeleted = assessment.deleted;
    this.assessmentId = assessment.id;

    // Set form fields
    if (assessment.fields !== null)
    {
      this.fields = JSON.parse(Base64Util.decodeUnicode(assessment.fields));
    }
    else
    {
      this.fields = [];
    }

    this.loadAssessment(assessment);

    const organizations: Organization[] = [];
    if (assessment.organizations !== null && assessment.organizations !== undefined)
    {
      assessment.organizations.forEach((org: any) =>
      {
        const fullOrganization: Organization = this.organizationsFiltered.find(x => x.id === org.organizationId);
        if (fullOrganization)
        {
          organizations.push(fullOrganization);
        }
      });
    }

    // Set form
    this.editForm = this.fb.group({
      id: [null],
      name: [
        { value: '', disabled: this.isDeleted },
        {
          validators: [Validators.required]
        }
      ],
      description: [{ value: null, disabled: this.isDeleted }, Validators.required],
      active: [{ value: true, disabled: this.isDeleted }],
      type: [{ value: null, disabled: this.isDeleted }, Validators.required],
      source: [{ value: null, disabled: !this.isNew || this.isDeleted }, Validators.required],
      documentName: [{ value: null, disabled: true }, Validators.required],
      assessmentVersion: [{ value: null, disabled: this.isDeleted }, Validators.required],
      sensitivities: [{ value: assessment.sensitivities, disabled: this.isDeleted }],
      privateAssessment: [
        { value: assessment.privateAssessment, disabled: this.isDeleted }, Validators.required
      ],
      workflowOnly: [
        { value: assessment.workflowOnly, disabled: this.isDeleted }, Validators.required
      ],
      showMemberExtension: [
        { value: assessment.showMemberExtension, disabled: this.isDeleted }, Validators.required
      ],
      organizations: [
        { value: organizations, disabled: this.isDeleted }
      ],      
      organizationalSpecific: [
        { value: assessment.organizationalSpecific, disabled: this.isDeleted }, Validators.required
      ],
    });

    this.updateOrganizationsValidator();
    this.editForm.get('organizationalSpecific').valueChanges.subscribe({
      next: () => {
        this.updateOrganizationsValidator();
      }
    });
    this.editForm.get('organizations').valueChanges.subscribe({
      next: () => {
        this.updateOrganizationsValidator();
      }
    });    

    this.editForm.get('source').valueChanges.subscribe({
      next: (source: string) =>
      {
        if (source === 'STATIC')
        {
          this.editForm.get('documentName').setValidators([Validators.required]);
        }
        else
        {
          this.editForm.get('documentName').setValidators([]);
        }

        if (this.tabstrip !== undefined)
        {
          // Must used within a setTimeout to allow showing the tab prior selection.
          setTimeout(() => { this.tabstrip.selectTab(1); });
        }
        this.editForm.get('documentName').updateValueAndValidity();
      }
    });

    this.assessmentService.getCachedAssessmentTypes().subscribe({
      next: (data) =>
      {
        this.allAssessmentTypes = data;

        // Load assessment types
        if (this.isNew)
        {
          this.assessmentTypes = this.allAssessmentTypes;
        }
        else
        {
          // On edit, we allow selecting assessment type with the same consent type
          this.assessmentTypes = this.allAssessmentTypes.filter(x => x.consentType === assessment.type.consentType);
        }

        this.editForm.reset(assessment);
        this.assessmentEdit = assessment;

        this.staff = sortBy(assessment.users, ['fullName']);
        this.view.state.skip = 0;
        this.view.setData(this.staff);
        this.count = this.staff.length;

        this.editFormActive = true;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Set the selected file.
   *
   * @param file Selected file.
   */
  private setFileFromBlob(file: Blob): void
  {
    const reader = new FileReader();
    reader.addEventListener('load', () =>
    {
      this.selectedFile = reader.result;
    }, false);
    reader.readAsDataURL(file);
  }

  /**
   * Set the selected file.
   *
   * @param file Selected file.
   */
  private setImportFileFromBlob(customFile: CustomFile, file: Blob): void
  {
    const reader = new FileReader();
    reader.addEventListener('load', () =>
    {
      const base64: any = reader.result;
      customFile.data = base64.split('base64,')[1];
      const importedData = JSON.parse(Base64.decode(customFile.data));
      if (importedData.name)
      {
        // Set properties
        this.name.setValue(importedData.name);
        this.description.setValue(importedData.description);
        this.source.setValue(importedData.source);
        this.assessmentVersion.setValue(importedData.assessmentVersion);
        this.sensitivities.setValue(importedData.sensitivities);
        this.privateAssessment.setValue(importedData.privateAssessment);
        this.organizationalSpecific.setValue(importedData.organizationalSpecific);
        this.workflowOnly.setValue(importedData.workflowOnly);
        this.showMemberExtension.setValue(importedData.showMemberExtension);

        // Set users
        const staff = [];
        if (importedData.users && importedData.users.length > 0)
        {
          importedData.users.forEach((u: any) =>
          {
            const userObjectFound = this.staffList.filter(user => user.id === u.id && user.fullName === u.fullName);
            if (userObjectFound !== undefined && userObjectFound.length === 1)
            {
              staff.push(userObjectFound[0]);
            }
          });
        }
        this.staff = sortBy(staff, ['name']);
        this.view.state.skip = 0;
        this.view.setData(this.staff);
        this.count = this.staff.length;

        //todo set Organizational Specific        

        // Must be the same id and name to access the type
        const foundType = this.allAssessmentTypes.find(x => x.id === importedData.type.id);
        if (foundType !== undefined && foundType.name === importedData.type.name)
        {
          this.type.setValue(importedData.type);
        }

        // Load all sections if the source is FORM
        if (importedData.source === 'FORM')
        {
          this.fields = importedData.fields;

          // We need to load assessments used in task for validations
          const assessmentsState = {
            filter: {
              filters: [
                { field: 'deleted', operator: 'eq', value: false },
                { field: 'id', operator: 'neq', value: this.assessmentId !== null ? this.assessmentId : 0 },
              ], logic: 'and'
            },
            group: [], skip: 0, sort: [], take: 99999
          };

          this.assessmentListService.queryByState(assessmentsState, (assessments: any) =>
          {
            this.assessmentsLookup = assessments.data;

            // We need to load channels used in task for validations
            const channelsState = {
              filter: {
                filters: [
                  { field: 'status', operator: 'eq', value: 'Online' },
                ], logic: 'and'
              },
              group: [], skip: 0, sort: [], take: 99999
            };

            this.channelListService.queryByState(channelsState, (channels: any) =>
            {
              this.channelsLookup = channels.data;

              // Use query list in case we need to add more lookups in the future.
              const listQueries: any = [];
              listQueries.push(this.communityServiceCategoryService.getCommunityServiceCategoriesList());

              let queryIndex = 0;
              forkJoin(listQueries).subscribe({
                next: (responseList: []) =>
                {
                  responseList.forEach((q: any) =>
                  {
                    if (queryIndex === 0)
                    {
                      // Must load the reference tables for import validations
                      this.serviceCategoriesLookup = q;
                    }

                    if (queryIndex === listQueries.length - 1)
                    {
                      // Load imported data
                      this.loadAssessment(importedData, true);

                      // Show import message to the user if some sections are partially loaded
                      if (!this.needsFormValid || !this.flagsFormValid || !this.tasksFormValid ||
                        !this.serviceTypesFormValid || !this.channelsFormValid)
                      {
                        this.translate.get(['ASSESSMENT-DATA-PARTIALLY-LOADED', 'NEEDS', 'FLAGS', 'TASKS', 'CHANNELS',
                          'RISK-SCORES']).subscribe({
                            next: (res) =>
                            {
                              const invalidSections = [];
                              if (!this.needsFormValid) { invalidSections.push(res.NEEDS); }
                              if (!this.flagsFormValid) { invalidSections.push(res.FLAGS); }
                              if (!this.tasksFormValid) { invalidSections.push(res.TASKS); }
                              if (!this.channelsFormValid) { invalidSections.push(res.CHANNELS); }
                              if (!this.serviceTypesFormValid) { invalidSections.push(res['RISK-SCORES']); }

                              this.messageHandlerService.displayDirectMessage('error',
                                res['ASSESSMENT-DATA-PARTIALLY-LOADED'].replace('{{value}}', invalidSections.join()));
                            }
                          });
                      }
                    }
                    queryIndex += 1;
                  });
                }
              });
            });
          });
        }
        else
        {
          this.flags = [];
          this.tasks = [];
          this.serviceTypes = [];
          this.channels = [];
          this.needs = [];
          this.fields = [];
        }
        this.editForm.markAsDirty();
      }
      else
      {
        this.messageHandlerService.displayError('IMPORT-INVALID-ASSESSMENT-FILE');
      }
    }, false);
    reader.readAsDataURL(file);
  }

  /**
   * Load assessments for all sections.
   *
   * @param assessment    Assessment object to load.
   * @param importContext True when import context to allow performing validations and resetting id to 0.
   */
  private loadAssessment(assessment: AssessmentEdit, importContext = false): void
  {
    // Prepare form flags
    let flagsFormValid = true;
    this.flags = assessment.flags === undefined ? [] : assessment.flags;
    this.flags.forEach(data =>
    {
      if (importContext)
      {
        data.id = null;
        data.guid = Guid.create().toString();
        data.version = 0;
        data.importError = false;

        // Validate category
        const flagCategory = this.flagCategoriesLookup.find(x =>
          x.id === data.category.id && x.code === data.category.code && x.codeSystem === data.category.codeSystem);
        if (flagCategory !== undefined)
        {
          data.category = flagCategory;
        }
        else
        {
          flagsFormValid = false;
          data.importError = true;
          data.category = null;
        }

        // Validate priority
        const flagPriority = this.flagPrioritiesLookup.find(x =>
          x.id === data.priority.id && x.code === data.priority.code && x.codeSystem === data.priority.codeSystem);
        if (flagPriority !== undefined)
        {
          data.priority = flagPriority;
        }
        else
        {
          flagsFormValid = false;
          data.importError = true;
          data.priority = null;
        }

        // Validate flag (if lookup based)
        if (data.flagType.id > 0)
        {
          const flagType = this.flagsLookup.find(x =>
            x.id === data.flagType.id && x.code === data.flagType.code && x.codeSystem === data.flagType.codeSystem);
          if (flagType !== undefined)
          {
            data.flagType = flagType;
          }
          else
          {
            flagsFormValid = false;
            data.importError = true;
            data.flagType = null;
          }
        }
      }
      const query = JSON.parse(Base64Util.decodeUnicode(data.query));  // Extract based64 and convert back to JSON object
      this.ruleService.convertRulesValueStringToArray(query);
      this.ruleService.convertRulesValueDateTimeToLocal(query);
      data.query = query;
      data.privacy = (data.privacy === 'shared') ? false : true;
    });
    this.flagsFormValid = flagsFormValid;

    // Prepare form tasks
    let tasksFormValid = true;
    this.tasks = assessment.tasks === undefined ? [] : assessment.tasks;
    this.tasks.forEach(
      data =>
      {
        if (importContext)
        {
          data.id = null;
          data.guid = Guid.create().toString();
          data.version = 0;
          data.importError = false;

          // Validate task assessment matching
          const taskAssessment = this.assessmentsLookup.find(x =>
            x.id === data.taskAssessment.id && x.name === data.taskAssessment.name && x.active && !x.deleted);
          if (taskAssessment !== undefined)
          {
            data.taskAssessment = taskAssessment;
          }
          else
          {
            tasksFormValid = false;
            data.importError = true;
            data.taskAssessment = null;
          }
        }
        const query = JSON.parse(Base64Util.decodeUnicode(data.query));  // Extract based64 and convert back to JSON object
        this.ruleService.convertRulesValueStringToArray(query);
        this.ruleService.convertRulesValueDateTimeToLocal(query);
        data.query = query;
        data.priority = this.taskService.listPriorities.find(x => x.value === data.priority);
      });
    this.tasksFormValid = tasksFormValid;

    // Prepare form service types
    let serviceTypesFormValid = true;
    this.serviceTypes = assessment.serviceTypes === undefined ? [] : assessment.serviceTypes;
    this.serviceTypes.forEach(
      data =>
      {
        if (importContext)
        {
          data.id = null;
          data.guid = Guid.create().toString();
          data.version = 0;
          data.importError = false;

          // Validate service type matching
          const serviceType = this.serviceTypesLookup.find(x =>
            x.id === data.serviceType.id && x.code === data.serviceType.code && x.codeSystem === data.serviceType.codeSystem);
          if (serviceType !== undefined)
          {
            data.serviceType = serviceType;
          }
          else
          {
            serviceTypesFormValid = false;
            data.importError = true;
            data.serviceType = null;
          }
        }
        const query = JSON.parse(Base64Util.decodeUnicode(data.query));  // Extract based64 and convert back to JSON object
        this.ruleService.convertRulesValueStringToArray(query);
        this.ruleService.convertRulesValueDateTimeToLocal(query);
        data.query = query;
      });
    this.serviceTypesFormValid = serviceTypesFormValid;

    // Prepare form channels
    let channelsFormValid = true;
    this.channels = assessment.channels === undefined ? [] : assessment.channels;
    this.channels.forEach(
      data =>
      {
        if (importContext)
        {
          data.id = null;
          data.guid = Guid.create().toString();
          data.version = 0;
          data.importError = false;

          // Validate channel matching
          const channel = this.channelsLookup.find(x =>
            x.id === data.channel.id && x.name === data.channel.name && x.status === 'Online');
          if (channel !== undefined)
          {
            data.channel = channel;
          }
          else
          {
            channelsFormValid = false;
            data.importError = true;
            data.channel = null;
          }
        }
        const query = JSON.parse(Base64Util.decodeUnicode(data.query));  // Extract based64 and convert back to JSON object
        this.ruleService.convertRulesValueStringToArray(query);
        this.ruleService.convertRulesValueDateTimeToLocal(query);
        data.query = query;

        const priority = this.caseUtilityService.distinctPriorities.filter(x => parseInt(x.priority, 10) === data.priority);
        data.priority = priority.length === 1 ? priority[0] : null;
      });
    this.channelsFormValid = channelsFormValid;

    // Prepare form needs
    let needsFormValid = true;
    this.needs = assessment.needs === undefined ? [] : assessment.needs;
    this.needs.forEach(
      data =>
      {
        if (importContext)
        {
          data.id = null;
          data.guid = Guid.create().toString();
          data.version = 0;
          data.importError = false;

          // Validate service catogories matching
          const newServiceCategories = [];
          data.serviceCategories.forEach((category) =>
          {
            const lookupCategory = this.serviceCategoriesLookup.find(x =>
              x.id === category.id && x.name === category.name && x.active &&
              x.serviceType.id === category.serviceType.id && x.serviceType.code === category.serviceType.code);
            if (lookupCategory !== undefined)
            {
              newServiceCategories.push(lookupCategory);
            }
            else
            {
              needsFormValid = false;
              data.importError = true;
            }
          });
          data.serviceCategories = newServiceCategories;
        }
        const query = JSON.parse(Base64Util.decodeUnicode(data.query));  // Extract based64 and convert back to JSON object
        this.ruleService.convertRulesValueStringToArray(query);
        this.ruleService.convertRulesValueDateTimeToLocal(query);
        data.query = query;
      });
    this.needsFormValid = needsFormValid;
  }
}
